import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Fish, GraduationCap, Utensils, HeartHandshake, Ticket, Bus, Gift, ArrowUpRight, Send,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NEXT_MOVE_EXPERIENCES = [
  { icon: Fish, name: "A Day on the Water", line: "A first cast, a first \u201cI did it.\u201d", amount: "$150" },
  { icon: GraduationCap, name: "A College Campus Visit", line: "The day \u201csomeday\u201d starts to feel possible.", amount: "$250" },
  { icon: Utensils, name: "A Shared Meal", line: "A full plate and a table where they belong.", amount: "$75" },
  { icon: HeartHandshake, name: "Meet a Mentor", line: "One adult who says \u201cI believe in you.\u201d", amount: "$200" },
  { icon: Ticket, name: "A Culture & Museum Day", line: "A door to a world they\u2019ve never seen.", amount: "$120" },
  { icon: Bus, name: "Getting There", line: "Sometimes hope just needs a ride.", amount: "$60" },
  { icon: Gift, name: "Sponsor a Custom Experience", line: "Tell us how you\u2019d love to help.", amount: "Any" },
];

const NEXT_MOVE_CONFIG = {
  program: "Next Move",
  testidPrefix: "nm",
  variant: "light",
  accent: "#0891b2",
  eyebrow: "Sponsor an Experience",
  titleLead: "Give a moment.",
  titleAccent: "Change a story.",
  blurb:
    "You can\u2019t fix everything a child has been through. But you can give them one afternoon that says their story isn\u2019t over \u2014 and that someone believes in what\u2019s next. Choose an experience to make possible for a young person or family.",
  dialogEyebrow: "Pledge an Experience",
  ctaVerb: "Pledge This",
  submitVerb: "Make This Pledge",
  experiences: NEXT_MOVE_EXPERIENCES,
};

export default function ExperienceWishlist({ config }) {
  const c = { ...NEXT_MOVE_CONFIG, ...(config || {}) };
  const dark = c.variant === "dark";
  const accent = c.accent;
  const P = c.testidPrefix;

  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ amount: "", name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const open = (exp) => {
    setSelected(exp);
    setForm({ amount: exp.amount === "Any" ? "" : exp.amount, name: "", email: "", phone: "", message: "" });
    setDone(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/pledges`, {
        program: c.program,
        experience: selected.name,
        amount: form.amount || null,
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        message: form.message || null,
      });
      setDone(true);
      toast.success("Thank you \u2014 your pledge is in. We\u2019ll be in touch soon.");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail
          ? String(err.response.data.detail)
          : "Something went wrong. Please try again or email partners@tandemtfoundation.org."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "mt-1 w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-slate-900 focus:outline-none focus:ring-2";
  const ringStyle = { "--tw-ring-color": accent };

  return (
    <section className={`py-20 md:py-28 px-6 md:px-10 ${dark ? "" : ""}`} data-testid={`${P}-wishlist`}>
      <div className="max-w-[1300px] mx-auto">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto">
            <p className="overline" style={{ color: dark ? "#f2c8ca" : accent }}>{c.eyebrow}</p>
            <h2 className={`font-serif font-light text-4xl md:text-6xl mt-4 leading-[1.05] tracking-tight ${dark ? "text-[#f7f5f0]" : "text-slate-900"}`}>
              {c.titleLead} <span className="italic" style={{ color: dark ? "#f2efe9" : accent }}>{c.titleAccent}</span>
            </h2>
            <p className={`font-light leading-relaxed mt-6 text-lg ${dark ? "text-[#f2efe9]/80" : "text-slate-600"}`}>
              {c.blurb}
            </p>
          </div>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {c.experiences.map((exp, i) => {
            const Icon = exp.icon;
            const custom = exp.amount === "Any";
            return (
              <Reveal key={exp.name} delay={(i % 3) * 0.06}>
                <div
                  data-testid={`${P}-wish-${i}`}
                  className={`h-full flex flex-col rounded-[1.5rem] p-8 transition-all ${
                    custom
                      ? "text-white"
                      : dark
                      ? "bg-white/[0.06] border border-white/12 hover:bg-white/[0.09]"
                      : "bg-white ring-1 ring-slate-200/70 shadow-sm hover:shadow-lg"
                  }`}
                  style={custom ? { backgroundColor: accent } : undefined}
                >
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center"
                    style={
                      custom
                        ? { backgroundColor: "rgba(255,255,255,0.18)", color: "#fff" }
                        : dark
                        ? { backgroundColor: "rgba(255,255,255,0.08)", color: accent }
                        : { backgroundColor: `${accent}14`, color: accent }
                    }
                  >
                    <Icon size={22} />
                  </div>
                  <h3 className={`font-serif font-light text-2xl mt-5 leading-snug ${custom ? "text-white" : dark ? "text-[#f7f5f0]" : "text-slate-900"}`}>
                    {exp.name}
                  </h3>
                  <p className="font-serif italic text-lg mt-2" style={{ color: custom ? "rgba(255,255,255,0.92)" : dark ? "#f2efe9" : accent }}>
                    {exp.line}
                  </p>
                  <div className={`mt-6 pt-5 flex items-center justify-between gap-4 border-t ${dark && !custom ? "border-white/12" : "border-current/10"}`}>
                    <span className={`overline text-[11px] ${custom ? "text-white/80" : dark ? "text-[#f2efe9]/70" : "text-slate-500"}`}>
                      {custom
                        ? "Any amount"
                        : String(exp.amount).startsWith("$")
                        ? `Sponsor from ${exp.amount}`
                        : exp.amount}
                    </span>
                    <button
                      onClick={() => open(exp)}
                      data-testid={`${P}-wish-pledge-${i}`}
                      className="group inline-flex items-center gap-2 overline text-[11px] px-5 py-3 rounded-full transition-colors text-white"
                      style={{ backgroundColor: custom ? "#ffffff" : accent, color: custom ? accent : "#fff" }}
                    >
                      {c.ctaVerb} <ArrowUpRight size={14} className="hover-arrow" />
                    </button>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="sm:max-w-lg" data-testid={`${P}-pledge-dialog`}>
          {done ? (
            <div className="py-6 text-center" data-testid={`${P}-pledge-success`}>
              <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center text-white" style={{ backgroundColor: accent }}>
                <HeartHandshake size={26} />
              </div>
              <DialogTitle className="font-serif font-light text-3xl mt-5">Thank you.</DialogTitle>
              <p className="text-slate-600 font-light mt-3">
                Your pledge of <strong className="text-slate-900">{selected?.name}</strong> is in.
                We&rsquo;ll reach out within a couple of business days to coordinate the details.
              </p>
              <button
                onClick={() => setSelected(null)}
                data-testid={`${P}-pledge-close`}
                className="mt-6 overline px-6 py-4 text-white transition-opacity hover:opacity-90 rounded-full"
                style={{ backgroundColor: accent }}
              >
                Close
              </button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <p className="overline" style={{ color: accent }}>{c.dialogEyebrow}</p>
                <DialogTitle className="font-serif font-light text-3xl">{selected?.name}</DialogTitle>
                <DialogDescription className="font-serif italic text-base" style={{ color: accent }}>
                  {selected?.line}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4 mt-2" data-testid={`${P}-pledge-form`}>
                <div>
                  <label className="overline text-[10px] text-slate-500">Your Pledge {selected?.amount !== "Any" && "(suggested)"}</label>
                  <input
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    placeholder={selected?.amount === "Any" ? "Amount or in-kind gift" : selected?.amount}
                    data-testid={`${P}-pledge-amount`}
                    className={inputCls}
                    style={ringStyle}
                  />
                </div>
                <div>
                  <label className="overline text-[10px] text-slate-500">Name / Business *</label>
                  <input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                    data-testid={`${P}-pledge-name`}
                    className={inputCls}
                    style={ringStyle}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="overline text-[10px] text-slate-500">Email *</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      required
                      data-testid={`${P}-pledge-email`}
                      className={inputCls}
                      style={ringStyle}
                    />
                  </div>
                  <div>
                    <label className="overline text-[10px] text-slate-500">Phone</label>
                    <input
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      data-testid={`${P}-pledge-phone`}
                      className={inputCls}
                      style={ringStyle}
                    />
                  </div>
                </div>
                <div>
                  <label className="overline text-[10px] text-slate-500">Message (optional)</label>
                  <textarea
                    rows={3}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    data-testid={`${P}-pledge-message`}
                    className={`${inputCls} resize-none`}
                    style={ringStyle}
                  />
                </div>
                <DialogFooter>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid={`${P}-pledge-submit`}
                    className="group w-full inline-flex items-center justify-center gap-3 overline px-8 py-5 text-white hover:opacity-90 transition-opacity rounded-full disabled:opacity-50"
                    style={{ backgroundColor: accent }}
                  >
                    {submitting ? "Sending\u2026" : <>{c.submitVerb} <Send size={15} /></>}
                  </button>
                </DialogFooter>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
